import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppointment } from '../../contexts/AppointmentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, Video, Phone, MessageSquare, CheckCircle, XCircle, Clock, Activity, Stethoscope } from 'lucide-react';
import CancelAppointmentModal from '../../components/CancelAppointmentModal';
import JoinConsultationModal from '../../components/JoinConsultationModal';

const Appointments = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { getUpcomingAppointments, getPastAppointments, cancelAppointment } = useAppointment();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [showJoinModal, setShowJoinModal] = useState(false);

  // Mock data for testing consultation UI
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  const mockUpcomingAppointments = [
    {
      id: 'mock-1',
      date: now.toISOString(),
      time: `${currentHour}:${currentMinute.toString().padStart(2, '0')}`,
      doctor: 'Dr. Amina Yusuf',
      specialty: 'OB/GYN Specialist',
      type: 'Antenatal Check-up',
      mode: 'video',
      status: 'confirmed',
      scheduledTime: new Date(new Date().setHours(currentHour, currentMinute, 0, 0)),
      amount: 5000,
    },
    {
      id: 'mock-2',
      date: now.toISOString(),
      time: `${(currentHour + 1) % 24}:${currentMinute.toString().padStart(2, '0')}`,
      doctor: 'Dr. Chidi Okafor',
      specialty: 'Pediatrician',
      type: 'Child Health Review',
      mode: 'audio',
      status: 'confirmed',
      scheduledTime: new Date(new Date().setHours((currentHour + 1) % 24, currentMinute, 0, 0)),
      amount: 3500,
    },
    {
      id: 'mock-3',
      date: now.toISOString(),
      time: `${(currentHour + 2) % 24}:${currentMinute.toString().padStart(2, '0')}`,
      doctor: 'Dr. Fatima Bello',
      specialty: 'General Practitioner',
      type: 'General Consultation',
      mode: 'chat',
      status: 'confirmed',
      scheduledTime: new Date(new Date().setHours((currentHour + 2) % 24, currentMinute, 0, 0)),
      amount: 2500,
    },
    {
      id: 'mock-4',
      date: new Date('2026-03-08').toISOString(),
      time: '10:00 AM',
      doctor: 'Dr. Ibrahim Ahmed',
      specialty: 'OB/GYN Specialist',
      type: 'Pregnancy Consultation',
      mode: 'video',
      status: 'pending_doctor_confirmation',
      scheduledTime: new Date('2026-03-08T10:00:00'),
      amount: 4500,
    },
  ];

  const mockPastAppointments = [
    {
      id: 'past-1',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      time: '2:30 PM',
      doctor: 'Dr. Ngozi Eze',
      specialty: 'Pediatrician',
      type: 'Child Immunization',
      mode: 'video',
      status: 'completed',
      scheduledTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      amount: 3000,
    },
    {
      id: 'past-2',
      date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      time: '11:00 AM',
      doctor: 'Dr. Amina Yusuf',
      specialty: 'OB/GYN Specialist',
      type: 'Antenatal Check-up',
      mode: 'audio',
      status: 'completed',
      scheduledTime: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
      amount: 5000,
    },
  ];

  const upcomingAppointments = [...mockUpcomingAppointments, ...getUpcomingAppointments(user?.id)];
  const pastAppointments = [...mockPastAppointments, ...getPastAppointments(user?.id)];

  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = (reason) => {
    cancelAppointment(selectedAppointment.id, reason);
    setShowCancelModal(false);
    setActionMessage({ type: 'info', text: t('refundProcessedIfApplicable') });
    setTimeout(() => setActionMessage(null), 5000);
    setSelectedAppointment(null);
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'video':
        return <Video className="w-5 h-5" />;
      case 'audio':
        return <Phone className="w-5 h-5" />;
      case 'chat':
        return <MessageSquare className="w-5 h-5" />;
      default:
        return <Phone className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'confirmed': return 'bg-emerald-100 text-emerald-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const canJoinConsultation = (appointment) => {
    const appointmentDateTime = new Date(`${appointment.date} ${appointment.time}`);
    const now = new Date();
    const diffMinutes = (appointmentDateTime - now) / (1000 * 60);
    // Can join 15 minutes before to 30 minutes after scheduled time
    return diffMinutes >= -30 && diffMinutes <= 15 && appointment.status === 'confirmed';
  };

  const handleJoinConsultation = (appointment, mode) => {
    // Navigate to consultation interface based on mode
    navigate(`/patient/consultation/${appointment.id}`, { 
      state: { appointment: { ...appointment, mode } } 
    });
  };

  const handleStartJoining = (appointment) => {
    setSelectedAppointment(appointment);
    setShowJoinModal(true);
  };

  const handleSelectMode = (mode) => {
    handleJoinConsultation(selectedAppointment, mode);
    setShowJoinModal(false);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4 mb-2">
            <div className="p-2 md:p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl md:rounded-2xl shadow-lg shadow-emerald-500/30">
              <Activity className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                Care & Appointments
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">Manage your telemedicine consultations and appointments</p>
            </div>
          </div>
          
          {/* Quick Action Buttons */}
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={() => navigate('/patient/book-appointment')}
              className="flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all font-semibold text-sm shadow-md shadow-emerald-500/30"
            >
              <Calendar className="w-4 h-4 md:w-5 md:h-5" />
              Book Consultation
            </button>
          </div>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className={`p-4 mb-6 rounded-xl md:rounded-2xl border-2 shadow-lg ${
            actionMessage.type === 'success' 
              ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200' 
              : 'bg-gradient-to-r from-blue-50 to-sky-50 border-blue-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg shadow-md ${
                actionMessage.type === 'success' ? 'bg-emerald-500' : 'bg-blue-500'
              }`}>
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <p className={`font-semibold text-sm md:text-base ${
                actionMessage.type === 'success' ? 'text-emerald-900' : 'text-blue-900'
              }`}>
                {actionMessage.text}
              </p>
            </div>
          </div>
        )}

        {/* Upcoming Appointments */}
        <div className="mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">{t('upcomingAppointmentsTitle')}</h2>
          {upcomingAppointments.length > 0 ? (
            <div className="space-y-4 md:space-y-6">
              {upcomingAppointments.map(appointment => (
                <div key={appointment.id} className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 hover:shadow-xl transition-shadow">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex items-start gap-3 md:gap-4 flex-1">
                      <div className="bg-gradient-to-br from-emerald-100 to-green-100 p-3 md:p-4 rounded-xl min-w-[70px] md:min-w-[80px] shadow-md">
                        <div className="text-center">
                          <Calendar className="w-5 h-5 md:w-6 md:h-6 text-emerald-600 mx-auto mb-1" />
                          <div className="text-xl md:text-2xl font-bold text-gray-900">
                            {new Date(appointment.date).getDate()}
                          </div>
                          <div className="text-xs text-gray-600 uppercase">
                            {new Date(appointment.date).toLocaleDateString('en-US', { month: 'short' })}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">{appointment.type}</h3>
                            <p className="text-xs md:text-sm text-gray-600 truncate">{appointment.doctor} - {appointment.specialty}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap self-start ${getStatusColor(appointment.status)}`}>
                            {appointment.status}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600 mt-3">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {appointment.time}
                          </span>
                          <span className="flex items-center gap-2">
                            {getModeIcon(appointment.mode)}
                            {t(`appointments.mode.${appointment.mode}`)}
                          </span>
                          {appointment.amount && (
                            <span className="text-emerald-600 font-bold">
                              ₦{appointment.amount.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 lg:flex-shrink-0">
                      {appointment.status === 'pending_doctor_confirmation' ? (
                        <div className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-xl text-xs md:text-sm font-semibold border border-yellow-200 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Awaiting Doctor Confirmation
                        </div>
                      ) : canJoinConsultation(appointment) ? (
                        <button 
                          onClick={() => handleStartJoining(appointment)}
                          className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all text-xs md:text-sm font-bold shadow-lg shadow-emerald-500/30 hover:scale-105"
                        >
                          <Stethoscope className="w-4 md:w-5 h-4 md:h-5" />
                          Join Consultation
                        </button>
                      ) : (
                        <button 
                          disabled
                          className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-gray-100 text-gray-400 rounded-xl cursor-not-allowed text-xs md:text-sm font-semibold"
                        >
                          <Clock className="w-4 h-4" />
                          Scheduled
                        </button>
                      )}
                      <button
                        onClick={() => handleCancelClick(appointment)}
                        className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all text-xs md:text-sm font-semibold border border-red-200"
                      >
                        <XCircle className="w-4 h-4" />
                        {t('cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8 text-center">
              <Calendar className="w-12 h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-3" />
              <p className="text-sm md:text-base text-gray-600 mb-4">{t('noUpcomingAppointments')}</p>
              <button
                onClick={() => navigate('/patient/book-appointment')}
                className="inline-block px-5 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all font-semibold text-sm md:text-base shadow-md shadow-emerald-500/30"
              >
                {t('bookConsultation')}
              </button>
            </div>
          )}
        </div>

        {/* Past Appointments */}
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">{t('pastAppointmentsTitle')}</h2>
          <div className="space-y-4 md:space-y-6">
            {pastAppointments.length > 0 ? (
              pastAppointments.map(appointment => (
                <div key={appointment.id} className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 hover:shadow-xl transition-shadow">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    <div className="flex items-start gap-3 md:gap-4 flex-1">
                      <div className={`p-3 md:p-4 rounded-xl min-w-[70px] md:min-w-[80px] shadow-md ${
                        appointment.status === 'completed' 
                          ? 'bg-gradient-to-br from-blue-100 to-sky-100' 
                          : 'bg-gradient-to-br from-gray-100 to-gray-200'
                      }`}>
                        <div className="text-center">
                          <Calendar className={`w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 ${appointment.status === 'completed' ? 'text-blue-600' : 'text-gray-600'}`} />
                          <div className="text-xl md:text-2xl font-bold text-gray-900">
                            {new Date(appointment.date).getDate()}
                          </div>
                          <div className="text-xs text-gray-600 uppercase">
                            {new Date(appointment.date).toLocaleDateString('en-US', { month: 'short' })}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">{appointment.type}</h3>
                            <p className="text-xs md:text-sm text-gray-600 truncate">{appointment.doctor} - {appointment.specialty}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap self-start ${getStatusColor(appointment.status)}`}>
                            {appointment.status}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600 mt-2">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {appointment.time}
                          </span>
                          <span className="flex items-center gap-2">
                            {getModeIcon(appointment.mode)}
                            {appointment.mode}
                          </span>
                        </div>
                        
                        {appointment.status === 'completed' && appointment.notes && (
                          <div className="mt-3 p-3 md:p-4 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl border-2 border-blue-200">
                            <p className="text-xs font-bold text-blue-900 mb-1">{t('doctorsNotes')}</p>
                            <p className="text-xs md:text-sm text-blue-800">{appointment.notes}</p>
                          </div>
                        )}
                        
                        {appointment.status === 'cancelled' && appointment.cancelReason && (
                          <div className="mt-3 p-3 md:p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border-2 border-red-200">
                            <p className="text-xs font-bold text-red-900 mb-1">{t('cancellationReason')}</p>
                            <p className="text-xs md:text-sm text-red-800">{appointment.cancelReason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8 text-center">
                <p className="text-sm md:text-base text-gray-600">{t('noPastAppointments')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && selectedAppointment && (
        <CancelAppointmentModal
          appointment={selectedAppointment}
          onConfirm={handleCancelConfirm}
          onClose={() => {
            setShowCancelModal(false);
            setSelectedAppointment(null);
          }}
        />
      )}

      {/* Join Consultation Modal */}
      {showJoinModal && selectedAppointment && (
        <JoinConsultationModal
          appointment={selectedAppointment}
          onSelectMode={handleSelectMode}
          onClose={() => {
            setShowJoinModal(false);
            setSelectedAppointment(null);
          }}
        />
      )}
    </div>
  );
};

export default Appointments;
